import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { ApiError } from '../../api/client';
import { platformApi, type PlatformTenantRow } from '../../api/platform';
import {
  Alert,
  Card,
  EmptyState,
  LoadingState,
  PageHeader,
  PageLayout,
  RowActions,
  StatusChip,
  Table,
} from '../../components';
import { useI18n, type TranslationKey } from '../../i18n';
import { useSession } from '../../auth/session-context';
import { formatDate, formatMoney } from '../../format';

/**
 * Kundenliste des Systemadmins (10_main_admin.md).
 *
 * Eine Tabelle, ein Klick in den Kunden. Keine Suche über einzelne
 * Datensätze - der Systemadmin verwaltet Konten, er arbeitet nicht in den
 * Daten der Kunden.
 */
export function PlatformTenantsPage(): JSX.Element {
  const { t, tError } = useI18n();
  const { handleApiError } = useSession();

  const [tenants, setTenants] = useState<PlatformTenantRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const laden = useCallback(async () => {
    setError(null);
    try {
      setTenants((await platformApi.listTenants()).tenants);
    } catch (caught) {
      handleApiError(caught);
      setError(caught instanceof ApiError ? tError(caught.code) : tError(undefined));
      setTenants([]);
    }
  }, [handleApiError, tError]);

  useEffect(() => {
    void laden();
  }, [laden]);

  return (
    <PageLayout>
      <PageHeader title={t('platform.tenants.title')} description={t('platform.tenants.paymentsLater')} />

      {error ? <Alert tone="error">{error}</Alert> : null}
      {tenants === null ? <LoadingState /> : null}

      {tenants !== null ? (
        <Card>
          {tenants.length === 0 ? (
            <EmptyState title={t('platform.tenants.empty')} />
          ) : (
            <Table
              label={t('platform.tenants.title')}
              headers={[
                t('platform.tenants.customer'),
                t('platform.tenants.status'),
                t('platform.tenants.callers'),
                t('platform.tenants.campaigns'),
                t('platform.tenants.balance'),
                t('platform.tenants.minutes'),
                '',
              ]}
            >
              {tenants.map((tenant) => (
                <tr key={tenant.id}>
                  <td>
                    <strong>{tenant.accountName ?? tenant.customerNumber}</strong>
                    <div className="field-hint">{tenant.ownerEmail}</div>
                    <div className="field-hint">
                      {t('platform.tenants.registered')}: {formatDate(tenant.registeredAt)}
                    </div>
                  </td>
                  <td>
                    <StatusChip tone={statusTon(tenant.status)}>
                      {t(('platform.status.' + tenant.status) as TranslationKey)}
                    </StatusChip>
                  </td>
                  <td>{tenant.callerCount}</td>
                  <td>{tenant.campaignCount}</td>
                  <td>
                    {formatMoney(tenant.balance)}
                    <div className="field-hint">{formatMoney(tenant.pricePerMinute, 3)}</div>
                  </td>
                  <td>{tenant.totalMinutes}</td>
                  <RowActions>
                    <Link className="btn btn-secondary" to={'/systemadmin/kunden/' + tenant.id}>
                      {t('platform.tenants.open')}
                    </Link>
                  </RowActions>
                </tr>
              ))}
            </Table>
          )}
        </Card>
      ) : null}
    </PageLayout>
  );
}

export function statusTon(status: string): 'erfolg' | 'gefahr' | 'neutral' {
  if (status === 'active') {
    return 'erfolg';
  }
  if (status === 'suspended') {
    return 'gefahr';
  }
  return 'neutral';
}
